import { queryOptions, useQuery } from '@tanstack/react-query';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import {
  getNextDetailsKey,
  getNextDetailsKeyRoute,
  INSTITUTION_DETAILS_TYPE,
  type InstitutionDetailsKeyForDetailsType,
  type InstitutionDetailsType,
} from '@/features/institution/utils';
import type { InstitutionDetailsPartial, InstitutionDetailsV2Partial, InstitutionDetailsVersion } from '@/services/api/profile';
import { useInstitutionDetailsAnswers, useInstitutionDetailsVersion } from './useInstitutionDetails';

type InstitutionDetailsAnswers = InstitutionDetailsPartial | InstitutionDetailsV2Partial | null | undefined;

export const detailsNextQueryKey = (
  pid: UserPid,
  detailType: InstitutionDetailsType,
  version: InstitutionDetailsVersion | undefined,
  institutionDetails: InstitutionDetailsAnswers,
  detailsPath: string,
) => ['institution', 'next-details-route', pid, detailType, version, institutionDetails, detailsPath];

const detailsNextQueryFn = async <T extends InstitutionDetailsType>(
  detailType: T,
  version: InstitutionDetailsVersion,
  institutionDetails: InstitutionDetailsAnswers,
  detailsPath: string,
) => {
  const nextDetailsKey = await getNextDetailsKey(detailType, version, institutionDetails);
  const nextDetailsKeyRoute = await getNextDetailsKeyRoute(detailType, version, institutionDetails);

  return nextDetailsKey
    ? {
        key: nextDetailsKey as InstitutionDetailsKeyForDetailsType<T>,
        route: nextDetailsKeyRoute ? `${detailsPath}/${nextDetailsKeyRoute}` : undefined,
      }
    : null;
};

const detailsNextQueryOptions = <T extends InstitutionDetailsType>(
  pid: UserPid,
  detailType: T,
  version: InstitutionDetailsVersion | undefined,
  institutionDetails: InstitutionDetailsAnswers,
  detailsPath: string,
) =>
  queryOptions({
    enabled: !!version,
    gcTime: 0,
    staleTime: 0,
    queryFn: () => detailsNextQueryFn(detailType, version as InstitutionDetailsVersion, institutionDetails, detailsPath),
    queryKey: detailsNextQueryKey(pid, detailType, version, institutionDetails, detailsPath),
  });

const useDetailsNext = <T extends InstitutionDetailsType>(detailType: T) => {
  const { pid } = useUserPid();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsAnswers();
  const { data: version } = useInstitutionDetailsVersion();
  const detailsPath = detailType === INSTITUTION_DETAILS_TYPE.ONBOARDING ? onboardingPath : `/institution/${detailType}`;
  const { isLoading: isLoadingQuery, data, ...rest } = useQuery(detailsNextQueryOptions(pid, detailType, version, institutionDetailsAnswers, detailsPath));
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingQuery;

  return {
    ...rest,
    data: data as { key: InstitutionDetailsKeyForDetailsType<T>; route: string } | null,
    isLoading,
    version,
  };
};

export { useDetailsNext };
